/* Vehicle selection and identity resolution for every storefront entry point. */
(function(root) {
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const text = value => typeof value === 'string' ? value.trim() : '';
  const storageKey = 'avid:vehicle-context';
  const queryKeys = ['vehicle_application_id','vehicle_model_id','vehicle_year','vehicle_make','vehicle_model','vehicle_platform','vehicle_trim'];
  function withNavigationContext(value, source = root.location?.href || 'https://avidrace.com') {
    const current = new URL(source), target = new URL(value, current);
    if (target.origin !== current.origin) return target;
    for (const key of ['currency','preview_theme_id','_avid_publication','_avid_release','_fd','pb']) {
      if (!target.searchParams.has(key) && current.searchParams.has(key)) target.searchParams.set(key,current.searchParams.get(key));
    }
    return target;
  }
  function directory(scope = root.document) {
    try { return normalizeGroups(JSON.parse(scope.querySelector('[data-catalog-vehicle-directory]')?.textContent || '[]')); }
    catch (_) { return []; }
  }
  function normalizeGroups(rows) {
    const groups = new Map();
    const blocked = new Set();
    for (const row of Array.isArray(rows) ? rows : []) {
      if (row?.schemaVersion !== 3 || !uuid.test(row.modelId) || !text(row.make) || !text(row.model) || !text(row.verificationFingerprint)) continue;
      let url;
      try { url = new URL(row.url, 'https://avidrace.com'); } catch (_) { continue; }
      if (url.origin !== 'https://avidrace.com' || !/^\/collections\/[a-z0-9-]+$/.test(url.pathname) || url.pathname === '/collections/all') continue;
      if (blocked.has(row.modelId)) continue;
      const group = groups.get(row.modelId) || {...row, url:url.pathname, applications:[]};
      if (group.verificationFingerprint !== row.verificationFingerprint || group.url !== url.pathname) { groups.delete(row.modelId); blocked.add(row.modelId); continue; }
      for (const application of Array.isArray(row.applications) ? row.applications : []) {
        if (!uuid.test(application?.applicationId) || !Number.isInteger(application.yearStart) || !Number.isInteger(application.yearEnd) || application.yearStart < 1900 || application.yearEnd < application.yearStart || application.yearEnd > 2200) continue;
        if (application.submodelId !== null && !uuid.test(application.submodelId)) continue;
        if (application.generationId !== null && !uuid.test(application.generationId)) continue;
        if (!application.configuration || typeof application.configuration !== 'object' || Array.isArray(application.configuration)) continue;
        if (!group.applications.some(item => item.applicationId === application.applicationId)) group.applications.push(application);
      }
      if (group.applications.length) groups.set(row.modelId, group);
    }
    return [...groups.values()].sort((a,b) => a.make.localeCompare(b.make) || a.model.localeCompare(b.model));
  }
  const applicationLabel = application => [application.trim || 'Submodel not specified', application.generation,
    ...Object.entries(application.configuration || {}).map(([key,value]) => `${key.replaceAll('_',' ')}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)].filter(Boolean).join(' · ');
  function resolve(value, groups = directory()) {
    const year = Number(value?.year);
    if (!Number.isInteger(year) || year < 1900 || year > 2200 || !uuid.test(value?.applicationId || '')) return null;
    const matches = groups.flatMap(group => group.applications.filter(application => application.applicationId === value.applicationId && year >= application.yearStart && year <= application.yearEnd).map(application => ({group,application})));
    if (matches.length !== 1) return null;
    const {group,application} = matches[0];
    if (value.modelId && value.modelId !== group.modelId) return null;
    return {applicationId:application.applicationId, modelId:group.modelId, year, make:group.make, model:group.model, platform:group.model,
      submodelId:application.submodelId, trim:application.trim, generationId:application.generationId,
      configuration:application.configuration, identityConfirmed:true, verificationFingerprint:group.verificationFingerprint};
  }
  function writeContext(value, storage) {
    const context = resolve(value);
    if (!context) return null;
    try { (storage || root.sessionStorage).setItem(storageKey, JSON.stringify({...context,expiresAt:Date.now()+30*60*1000})); } catch (_) { /* URL identity remains available. */ }
    return context;
  }
  function current(groups = directory()) {
    try {
      const params=new URL(root.location.href).searchParams;
      const urlValue=resolve({applicationId:params.get('vehicle_application_id'),modelId:params.get('vehicle_model_id'),year:params.get('vehicle_year')},groups);
      if(urlValue)return urlValue;
      const stored=JSON.parse(root.sessionStorage.getItem(storageKey)||'null');
      return Number(stored?.expiresAt)>Date.now()?resolve(stored,groups):null;
    }catch(_){return null;}
  }
  function select(value) {
    const context = writeContext(value);
    if (!context) return null;
    const url = new URL(root.location.href);
    queryKeys.forEach(key => url.searchParams.delete(key));
    const selectedUrl = new URL(destination({url:url.pathname},context),url);
    selectedUrl.searchParams.forEach((value,key) => url.searchParams.set(key,value));
    root.history.replaceState(root.history.state,'',url);
    root.document.dispatchEvent(new CustomEvent('avid:vehicle-context-change',{detail:{context}}));
    return context;
  }
  function destination(group, context) {
    const url = withNavigationContext(group.url);
    queryKeys.forEach(key => url.searchParams.delete(key));
    url.searchParams.set('vehicle_application_id',context.applicationId);
    url.searchParams.set('vehicle_model_id',context.modelId);
    url.searchParams.set('vehicle_year',String(context.year));
    url.searchParams.set('vehicle_make',context.make);
    url.searchParams.set('vehicle_model',context.model);
    if (context.trim) url.searchParams.set('vehicle_trim',context.trim);
    return `${url.pathname}${url.search}`;
  }
  function bind(form, groups = directory(), signal, onSelect) {
    if (!form || form.dataset.catalogVehicleReady === 'true') return;
    form.dataset.catalogVehicleReady = 'true';
    const make = form.querySelector('[data-catalog-vehicle-make]');
    const model = form.querySelector('[data-catalog-vehicle-model]');
    const year = form.querySelector('[data-catalog-vehicle-year]');
    const application = form.querySelector('[data-catalog-vehicle-application]');
    const status = form.querySelector('[data-catalog-vehicle-status]');
    const submit = form.querySelector('[type=submit]');
    if (!make || !model || !year || !application) return;
    const options = (control, placeholder, entries) => {
      control.replaceChildren(new Option(placeholder,''), ...entries.map(row => new Option(row.label,row.value)));
      control.disabled = !entries.length;
    };
    const selected = () => groups.find(group => group.modelId === model.value && group.make === make.value);
    const ready = () => {
      const context = resolve({applicationId:application.value,modelId:model.value,year:year.value},groups);
      if (submit) submit.disabled = !context;
      if (status) status.textContent = context ? 'Check each product for compatibility and any installation conditions.' : 'Choose your make, model, year, and submodel.';
    };
    const updateApplications = () => {
      const matches = (selected()?.applications || []).filter(row => Number(year.value) >= row.yearStart && Number(year.value) <= row.yearEnd);
      const labels = matches.map(applicationLabel);
      const unique = matches.filter((row,index) => labels.filter(label => label === labels[index]).length === 1);
      options(application,'Choose submodel',unique.map(row => ({value:row.applicationId,label:applicationLabel(row)})));
      if (unique.length === 1) application.value = unique[0].applicationId;
      ready();
      if (status && matches.length > unique.length && !application.value) status.textContent = 'Some vehicle identities need review. Choose an available submodel or contact AVID.';
    };
    const updateYears = () => {
      const years = new Set();
      for (const row of selected()?.applications || []) for (let value=row.yearStart; value<=Math.min(row.yearEnd,new Date().getUTCFullYear()+1); value++) years.add(value);
      options(year,'Choose year',[...years].sort((a,b)=>b-a).map(value=>({value:String(value),label:String(value)})));
      updateApplications();
    };
    const updateModels = () => {
      options(model,'Choose model',groups.filter(group=>group.make===make.value).map(group=>({value:group.modelId,label:group.model})));
      updateYears();
    };
    options(make,'Choose make',[...new Set(groups.map(group=>group.make))].map(value=>({value,label:value})));
    make.addEventListener('change',updateModels,{signal}); model.addEventListener('change',updateYears,{signal});
    year.addEventListener('change',updateApplications,{signal}); application.addEventListener('change',ready,{signal});
    root.document.addEventListener('avid:vehicle-context-change',event=>{
      if (event.detail?.context === null) { make.value=''; updateModels(); }
    },{signal});
    form.addEventListener('submit',event=>{
      event.preventDefault();
      const group=selected(), context=writeContext({applicationId:application.value,modelId:model.value,year:year.value});
      if (!group || !context || !form.reportValidity()) return;
      if (onSelect) {
        // Inline finders must replace an older deep-link identity before the
        // product/cart consumers resolve context (URL takes precedence).
        const url=new URL(root.location.href);
        queryKeys.forEach(key=>url.searchParams.delete(key));
        const selectedUrl=new URL(destination(group,context),url);
        selectedUrl.searchParams.forEach((value,key)=>url.searchParams.set(key,value));
        root.history.replaceState(root.history.state,'',url);
        onSelect(context);
        root.document.dispatchEvent(new CustomEvent('avid:vehicle-context-change',{detail:{context}}));
      }
      else root.location.assign(destination(group,context));
    },{signal});
    updateModels();
    // Restore only a current published identity after navigation or section reload.
    let initial = null;
    try {
      const params = new URL(root.location.href).searchParams;
      initial = resolve({applicationId:params.get('vehicle_application_id'),modelId:params.get('vehicle_model_id'),year:params.get('vehicle_year')},groups);
      if (!initial) {
        const stored = JSON.parse(root.sessionStorage.getItem(storageKey) || 'null');
        if (Number(stored?.expiresAt) > Date.now()) initial = resolve(stored,groups);
      }
    } catch (_) { /* Selection remains available when session storage is restricted. */ }
    if (initial) {
      make.value=initial.make;updateModels();model.value=initial.modelId;updateYears();
      year.value=String(initial.year);updateApplications();application.value=initial.applicationId;ready();
    }
  }
  function browseContext(groups=directory()) {
    const params=new URL(root.location.href).searchParams;
    const modelId=params.get('vehicle_model_id');
    if(modelId&&!params.get('vehicle_application_id'))return groups.some(group=>group.modelId===modelId)?{modelId}:null;
    return current(groups);
  }
  function matchesDiscovery(projection,context) {
    if(!context)return true;
    if(projection?.contractVersion!==1||!Array.isArray(projection.rules))return false;
    const candidate=projection.rules.some(rule=>rule.modelId===context.modelId&&rule.status!=='retired'&&rule.status!=='incompatible'&&rule.approvalStatus!=='rejected'
      && (!context.applicationId||root.AvidCatalogFitment.catalogFitmentScopeMatches(rule,context)));
    const pending=projection.reviewRequired===true&&Array.isArray(projection.discoveryModelIds)&&projection.discoveryModelIds.includes(context.modelId);
    return (candidate||pending)&&(!context.applicationId||root.AvidCatalogFitment.evaluateProjectedCatalogFitment(projection.rules,context).outcome!=='incompatible');
  }
  function clear() {
    try{root.sessionStorage.removeItem(storageKey);}catch(_){/* Browsing works without storage. */}
    const url=new URL(root.location.href);queryKeys.forEach(key=>url.searchParams.delete(key));
    root.history.replaceState(null,'',`${url.pathname}${url.search}`);
    root.document.dispatchEvent(new CustomEvent('avid:vehicle-context-change',{detail:{context:null}}));
  }
  root.AvidVehicleSelector = {directory,normalizeGroups,applicationLabel,resolve,writeContext,current,select,destination,bind,queryKeys,browseContext,matchesDiscovery,clear,withNavigationContext};
  if (typeof module !== 'undefined' && module.exports) module.exports = root.AvidVehicleSelector;
})(typeof globalThis !== 'undefined' ? globalThis : this);
